import React, { useState } from 'react';
import './StatusToggle.css';

/**
 * On/off switch for a record's active status.
 *
 * Flips optimistically so the switch reacts instantly, then reverts if the
 * request fails. `onToggle` receives the next value and should return the API
 * promise (resolving falsy / rejecting on failure).
 */
const StatusToggle = ({ checked, onToggle, disabled = false, id }) => {
  const [busy, setBusy] = useState(false);
  const [optimistic, setOptimistic] = useState(null);

  const value = optimistic === null ? !!checked : optimistic;

  const handleChange = async () => {
    if (busy || disabled) return;
    const next = !value;
    setOptimistic(next);
    setBusy(true);
    try {
      const ok = await onToggle(next);
      if (ok === false) setOptimistic(!next);
    } catch {
      setOptimistic(!next);
    } finally {
      setBusy(false);
      // Let the refreshed list value take over again.
      setOptimistic(null);
    }
  };

  return (
    <label className={`status-toggle ${value ? 'on' : 'off'} ${busy || disabled ? 'is-disabled' : ''}`}>
      <input
        type="checkbox"
        checked={value}
        onChange={handleChange}
        disabled={busy || disabled}
        aria-label={value ? 'Active' : 'Inactive'}
        id={id}
      />
      <span className="status-toggle-track"><span className="status-toggle-thumb" /></span>
      <span className="status-toggle-text">{value ? 'Active' : 'Inactive'}</span>
    </label>
  );
};

export default StatusToggle;
